import { TEXT } from "@/constants/text";
import {
  PASSWORD_LOWERCASE_PATTERN,
  PASSWORD_MIN_LENGTH,
  PASSWORD_NUMBER_PATTERN,
  PASSWORD_REPEATED_CHARACTER_PATTERN,
  PASSWORD_SPECIAL_CHARACTER_PATTERN,
  PASSWORD_UPPERCASE_PATTERN,
} from "@/utils/validation";

const FAIR_TIER_MIN_MET_COUNT = 2;

export type PasswordRequirementKey =
  "minLength" | "mixedCase" | "noRepeats" | "specialChar";

interface PasswordRequirement {
  isMet: (password: string) => boolean;
  key: PasswordRequirementKey;
  label: string;
}

export const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  {
    isMet: (password) => password.length >= PASSWORD_MIN_LENGTH,
    key: "minLength",
    label: TEXT.auth.newPassword.requirementMinLength,
  },
  {
    isMet: (password) =>
      PASSWORD_LOWERCASE_PATTERN.test(password) &&
      PASSWORD_UPPERCASE_PATTERN.test(password) &&
      PASSWORD_NUMBER_PATTERN.test(password),
    key: "mixedCase",
    label: TEXT.auth.newPassword.requirementMixedCase,
  },
  {
    isMet: (password) => PASSWORD_SPECIAL_CHARACTER_PATTERN.test(password),
    key: "specialChar",
    label: TEXT.auth.newPassword.requirementSpecialChar,
  },
  {
    isMet: (password) =>
      password.length > 0 &&
      !PASSWORD_REPEATED_CHARACTER_PATTERN.test(password),
    key: "noRepeats",
    label: TEXT.auth.newPassword.requirementNoRepeats,
  },
];

export type RequirementStatus = "met" | "neutral" | "unmet";

export const getRequirementStatus = (
  isMet: boolean,
  isValidated: boolean
): RequirementStatus => {
  if (isMet) {
    return "met";
  }
  return isValidated ? "unmet" : "neutral";
};

export type PasswordStrengthTier = "fair" | "strong" | "weak";

interface PasswordStrength {
  metCount: number;
  tier: PasswordStrengthTier;
}

export const getPasswordStrength = (password: string): PasswordStrength => {
  const metCount = PASSWORD_REQUIREMENTS.filter((requirement) =>
    requirement.isMet(password)
  ).length;

  if (metCount >= PASSWORD_REQUIREMENTS.length) {
    return { metCount, tier: "strong" };
  }
  if (metCount >= FAIR_TIER_MIN_MET_COUNT) {
    return { metCount, tier: "fair" };
  }
  return { metCount, tier: "weak" };
};

export const isPasswordValid = (password: string): boolean =>
  PASSWORD_REQUIREMENTS.every((requirement) => requirement.isMet(password));
